import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveal — JS-gated scroll-float reveal wrapper (PRD-144).
 *
 * First PRODUCTION reveal (prototypes have their own). Critical difference from the
 * prototype `.cc-reveal`: this is JS-GATED. It renders fully VISIBLE by default
 * (bare `.da-reveal`, no opacity:0) and only ARMS the hidden/animatable state AFTER
 * mount in a real browser. This keeps no-JS users and the Puppeteer prerender snapshot
 * fully visible — load-bearing for SEO.
 *
 * The animatable look + reduced-motion guard live in CSS (global, see
 * DRIFTALARM_BASE_STYLES.css `.da-reveal` / `.is-armed` / `.is-shown`).
 *
 * @param {React.ReactNode} children
 * @param {string} className   extra classes merged after the reveal classes
 * @param {React.ElementType} as   element/tag to render (e.g. "a" for a flow box)
 * @param {number} delay        stagger in ms, exposed as `--reveal-delay`
 */
export default function Reveal({ children, className = '', as: Tag = 'div', delay = 0, ...rest }) {
  const ref = useRef(null);
  const [armed, setArmed] = useState(false); // false => fully visible (no-JS/prerender safe)
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const reduce = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // CRITICAL SEO GUARD: never ARM during the Puppeteer prerender (or for headless /
    // reduced-motion / no-IO). If we armed here, prerender.js would capture below-fold
    // boxes frozen at opacity:0 in build/how-it-works/index.html -> invisible to no-JS
    // users (it never scrolls to fire the IO). Leaving armed=false keeps the bare
    // `.da-reveal` (fully visible) in the snapshot; a real browser re-runs this effect
    // on hydration (webdriver=false) and animates normally.
    const headless = typeof navigator !== 'undefined' && navigator.webdriver;
    if (headless || reduce || typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return undefined; // armed stays FALSE => visible, no animation
    }

    setArmed(true); // arm AFTER mount only (real browsers only)
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setShown(true);
        io.disconnect();
      }
    }, { threshold: 0.1 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`da-reveal ${armed ? 'is-armed' : ''} ${shown ? 'is-shown' : ''} ${className}`.trim()}
      style={delay ? { '--reveal-delay': `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
}
